import React, { useState, useEffect } from 'react';
import '../styles/GenerateCategoryModal.scss';
import { getData, postData, ApiResponse, getUniqueCategoryName } from '../utils/api';
import WordsList, { WordItem } from '../components/WordsList';
import logger from '../utils/logger';
import { fetchDictionaryCategories, DictionaryCategory } from '../utils/api';
import Notification from './Notification';
import { NOTIFICATION_DURATION } from '../constants/notifications';
import { validateCategoryName } from '../utils/validation';

interface GeneratedWord {
  word: string;
  pinyin: string;
  translation: string;
  priority: number;
  example?: {
    sentence: string;
    pinyin: string;
    translation: string;
  };
}

interface GeneratedResponse {
  words: GeneratedWord[];
  emoji: string;
}

interface GenerateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated: (categoryName: string) => void;
}

const GenerateCategoryModal: React.FC<GenerateCategoryModalProps> = ({
  isOpen,
  onClose,
  onCategoryCreated
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [wordCount, setWordCount] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedWords, setGeneratedWords] = useState<WordItem[]>([]);
  const [generatedEmoji, setGeneratedEmoji] = useState<string>('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCategoryName('');
      setWordCount(20);
      setGeneratedWords([]);
      setGeneratedEmoji('');
      setError(null);
      setValidationError(null);
    }
  }, [isOpen]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryName(e.target.value);
    setValidationError(null);
  };

  const handleWordCountChange = (count: number) => {
    setWordCount(count);
  };

  const handleGenerate = async () => {
    const validationError = validateCategoryName(categoryName);
    if (validationError) {
      setValidationError(validationError);
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setIsGenerating(true);
    setError(null);
    setValidationError(null);

    try {
      // Получаем уникальное имя категории
      const uniqueCategoryName = await getUniqueCategoryName(categoryName.trim());
      setCategoryName(uniqueCategoryName);

      const response = await getData<GeneratedResponse>(`/ai/words?topic=${encodeURIComponent(uniqueCategoryName)}&count=${wordCount}`);
      
      if (response.success && response.data) {
        const mappedWords = response.data.words.map((word): WordItem => ({
          characters: word.word,
          pinyin: word.pinyin,
          translation: word.translation,
          priority: word.priority,
          example: word.example
        }));
        setGeneratedWords(mappedWords);
        setGeneratedEmoji(response.data.emoji);
      } else {
        setError('Что-то серверу нехорошо, попробуй позже');
      }
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Что-то серверу нехорошо, попробуй позже');
      } else {
        const errorMsg = err instanceof Error ? err.message : 'Неизвестная ошибка';
        setError(`Произошла ошибка: ${errorMsg}`);
      }
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!categoryName.trim() || generatedWords.length === 0) {
      setError('Нет слов для сохранения');
      return;
    }

    setIsLoading(true);
    setError(null);
    setValidationError(null);

    try {
      const response = await postData<any>('/dictionary/words/list', {
        words: generatedWords.map(word => word.characters),
        category: {
          name: categoryName.trim(),
          emoji: generatedEmoji
        },
        status: "IN_PROGRESS"
      });

      if (response.success) {
        logger.log('Список успешно создан:', categoryName);
        const createdCategoryName = categoryName.trim();
        onCategoryCreated(createdCategoryName);
        onClose();
      } else {
        setError('Что-то серверу нехорошо, попробуй позже');
      }
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Что-то серверу нехорошо, попробуй позже');
      } else {
        const errorMsg = err instanceof Error ? err.message : 'Неизвестная ошибка';
        setError(`Произошла ошибка: ${errorMsg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !generatedWords.length) {
      handleGenerate();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {error && (
        <Notification
          message={error}
          onClose={() => setError(null)}
          duration={NOTIFICATION_DURATION}
        />
      )}
      <div className="generate-category-modal-overlay" onClick={onClose}>
        <div className="generate-category-modal" onClick={(e) => e.stopPropagation()}>
          <h2>Генерация списка</h2>
          
          {generatedWords.length === 0 && (
            <>
              <div className="input-container">
                <input
                  id="category-name"
                  type="text"
                  value={categoryName}
                  onChange={handleNameChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Футбол"
                  className={`name-input ${validationError ? 'error' : ''}`}
                  autoFocus
                  disabled={isGenerating}
                />
              </div>

              <div className="word-count-container">
                <label>Сколько слов?</label>
                <div className="word-count-buttons">
                  {[10, 20, 30, 40, 50].map((count) => (
                    <button
                      key={count}
                      className={`word-count-button ${wordCount === count ? 'active' : ''}`}
                      onClick={() => handleWordCountChange(count)}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {generatedWords.length > 0 && (
            <div className="generated-words-container">
              <div className="generated-words-header">
                <span className="generated-emoji">{generatedEmoji}</span>
                <span className="generated-title">{categoryName}</span>
              </div>
              <WordsList 
                words={generatedWords} 
                className="generated-words-list"
                itemClassName="generated-word-item"
              />
            </div>
          )}
          
          <div className="modal-footer">
            <button 
              className="cancel-button" 
              onClick={onClose}
              disabled={isLoading}
            >
              Отмена
            </button>
            {generatedWords.length === 0 ? (
              <button 
                className="create-button" 
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? 'Генерация...' : 'Сгенерировать'}
              </button>
            ) : (
              <button 
                className="save-button" 
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? 'Сохранение...' : 'Сохранить'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GenerateCategoryModal; 